import { TableWithStoredSettingsAndLayoutSettings } from '@Components/Tables';
import { Permission } from '@Constants/Permission.g';
import { initialOrderSorting, orderColumns } from '@Constants/TableColumns';
import { useAppState, useHasPermission } from '@Contexts/appContext';
import { useGetOrders } from '@Hooks/Orders';
import { useTableMode } from '@Hooks/useTableModes';
import { type ApiOrder, EnumFrontendLayoutSetting } from '@Model';
import {
    type FilterExpression,
    type ITableProperties,
    type SelectOption,
    EnumFilterExpressionOperation,
    EnumOperation,
    SingleSelect,
} from '@synqo/frontend-component-library';
import { useCallback, useMemo } from 'react';

type WeighingTableModes = 'all' | 'hofliste';

const tableModes: SelectOption<WeighingTableModes>[] = [
    { name: 'all', label: 'Verwiegungen', value: 'all' },
    { name: 'hofliste', label: 'Hofliste', value: 'hofliste' },
];

const hoflisteColumnFilters: FilterExpression = {
    operation: EnumFilterExpressionOperation.And,
    filters: [
        {
            operation: EnumFilterExpressionOperation.Or,
            filters: [
                { field: 'Positions.WeighingSlips.TareWeight', value: '0', operator: EnumOperation.Equal },
                { field: 'Positions.WeighingSlips.GrossWeight', value: '0', operator: EnumOperation.Equal },
            ],
        },
        { field: 'ProcessState', value: '3', operator: EnumOperation.NotEqual },
        { field: 'ProcessState', value: '4', operator: EnumOperation.NotEqual },
    ],
};

export function FrameListWeighing() {
    const hasPermission = useHasPermission();
    const {
        current: { lastWeighedOrder },
    } = useAppState();
    const { tableModeValue, setTableMode, availableTableModes } = useTableMode<WeighingTableModes>('all', tableModes);

    const canCreate = hasPermission(Permission.AuftragsbearbeitungOrdersCreate);
    const hoflisteShown = tableModeValue === 'hofliste';

    const loadOrders = useGetOrders();

    const columnFilters = useMemo(() => (hoflisteShown ? hoflisteColumnFilters : undefined), [hoflisteShown]);

    const rowFlags = useMemo<ITableProperties<ApiOrder>['rowFlags']>(
        () => (lastWeighedOrder ? { [lastWeighedOrder]: { appearance: 'highlighted' } } : undefined),
        [lastWeighedOrder],
    );

    const toolbarElements = useCallback(
        (elements: React.ReactNode[]) => [
            ...elements,
            <SingleSelect<WeighingTableModes>
                key="tableMode"
                name="tableMode"
                items={availableTableModes}
                value={tableModeValue}
                onChange={setTableMode}
            />,
        ],
        [availableTableModes, tableModeValue, setTableMode],
    );

    return (
        <TableWithStoredSettingsAndLayoutSettings<ApiOrder>
            layoutSettingsContext={EnumFrontendLayoutSetting.WeighingsTableLayout}
            name="WeighingList"
            key={tableModeValue}
            columns={orderColumns}
            loader={loadOrders}
            additionalColumnFilters={columnFilters}
            canCreate={canCreate}
            useOnClickNavigation={true}
            toolbarElements={toolbarElements}
            rowFlags={rowFlags}
            fullSize
            initialSorting={initialOrderSorting}
        />
    );
}
